import { c as _c } from "react/compiler-runtime";
import { Text } from "@/components/ui/Text";
import type { LucideIcon } from "lucide-react";
import React from "react";
import cn from "twsx";
type EmptyStateProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  icon?: LucideIcon;
  compact?: boolean;
  tone?: "neutral" | "danger";
  className?: string;
};

/** Use when a list, panel or page has nothing to show yet. */
export function EmptyState(t0) {
  const $ = _c(21);
  const {
    title,
    description,
    action,
    icon: Icon,
    compact: t1,
    tone: t2,
    className
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const tone = t2 === undefined ? "neutral" : t2;
  const t3 = tone === "danger" ? "alert" : "status";
  const t4 = compact ? "gap-2 px-6 py-8" : "gap-3 px-6 py-12";
  let t5;
  if ($[0] !== className || $[1] !== t4) {
    t5 = cn("flex flex-col items-center text-center", t4, className);
    $[0] = className;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== Icon || $[4] !== compact || $[5] !== tone) {
    t6 = Icon ? <div className={cn("mb-1 flex items-center justify-center rounded-settings-list-view-item", "border border-border-primary bg-bg-secondary/60 shadow-low", compact ? "size-9" : "size-12", tone === "danger" ? "text-red-text" : "text-text-tertiary")}><Icon className={compact ? "size-4.5" : "size-6"} strokeWidth={1.75} /></div> : !compact && <EmptyIllustration />;
    $[3] = Icon;
    $[4] = compact;
    $[5] = tone;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = compact ? "md" : "lg";
  let t8;
  if ($[7] !== t7 || $[8] !== title) {
    t8 = <Text as="p" size={t7} weight="medium">{title}</Text>;
    $[7] = t7;
    $[8] = title;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== description) {
    t9 = description && <Text as="p" tone="tertiary" className="max-w-[360px]">{description}</Text>;
    $[10] = description;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== action) {
    t10 = action && <div className="mt-1 flex items-center gap-2">{action}</div>;
    $[12] = action;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t3 || $[16] !== t5 || $[17] !== t6 || $[18] !== t8 || $[19] !== t9) {
    t11 = <div role={t3} className={t5}>{t6}{t8}{t9}{t10}</div>;
    $[14] = t10;
    $[15] = t3;
    $[16] = t5;
    $[17] = t6;
    $[18] = t8;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
function EmptyIllustration() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg aria-hidden="true" width="96" height="72" viewBox="0 0 96 72" fill="none" className="mb-1"><rect x="8" y="10" width="80" height="52" rx="8" fill="var(--color-bg-secondary)" fillOpacity="0.3" stroke="var(--color-border-primary)" strokeWidth="0.5" /><rect x="18" y="22" width="44" height="6" rx="3" fill="var(--color-bg-secondary)" fillOpacity="0.6" /><rect x="18" y="34" width="60" height="6" rx="3" fill="var(--color-bg-secondary)" fillOpacity="0.45" /><rect x="18" y="46" width="32" height="6" rx="3" fill="var(--color-bg-secondary)" fillOpacity="0.3" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}